import { useEffect, useState } from 'react';
import { Field, Input } from '@/ui';
import { availabilityApi, usersApi } from '@/api';
import type { AvailabilityConfig, InspectorAvailabilityDay, User } from '@/types';
import {
  DAYS_OF_WEEK,
  DEFAULT_AVAILABILITY,
  detectBrowserTimezone,
  TIMEZONE_OPTIONS,
} from '@/pages/Settings/_constants';
import { StepLayout } from '../StepLayout';
import { getPublicOrigin } from '@/utils/booking';

interface BookingStepProps {
  profile: User;
  availability: AvailabilityConfig;
  onSaved: (user: User, availability: AvailabilityConfig) => void;
  onSkip: () => void;
}

// Mirrors the slugify in pages/Settings.tsx — kept in sync by hand because
// the Settings copy lives inside the file and isn't exported. Same regex set.
function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken';

export function BookingStep({ profile, availability, onSaved, onSkip }: BookingStepProps) {
  // Slug state — auto-derived from companyName until the user types.
  const [slug, setSlug] = useState(profile.slug ?? slugify(profile.companyName ?? ''));
  const [slugTouched, setSlugTouched] = useState(Boolean(profile.slug));
  const [slugCheck, setSlugCheck] = useState<{ status: SlugStatus; suggestion: string | null }>({
    status: 'idle',
    suggestion: null,
  });

  // The schema defaults profile.timezone to 'America/Los_Angeles' for every
  // new user, which would otherwise stomp browser detection. Treat the system
  // default as "unset" so the user lands on their actual zone and only sees
  // LA if that's truly where they are.
  const [timezone, setTimezone] = useState(
    profile.timezone && profile.timezone !== 'America/Los_Angeles'
      ? profile.timezone
      : detectBrowserTimezone(),
  );

  // Availability — start from saved data if any, else the Mon–Fri 9–5 default.
  const [days, setDays] = useState<InspectorAvailabilityDay[]>(() => {
    if (availability.days.length === 0) return DEFAULT_AVAILABILITY;
    return DEFAULT_AVAILABILITY.map((d) => {
      const match = availability.days.find((s) => s.dayOfWeek === d.dayOfWeek);
      return match ?? { ...d, isActive: false };
    });
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced slug-availability check. Same 500 ms cadence as Settings so the
  // experience is consistent and the backend gets the same load characteristics.
  useEffect(() => {
    const trimmed = slug.trim();
    if (trimmed.length < 3) {
      setSlugCheck({ status: 'idle', suggestion: null });
      return;
    }
    setSlugCheck((prev) => ({ ...prev, status: 'checking' }));
    const timer = setTimeout(async () => {
      try {
        const resp = await usersApi.checkSlug(trimmed);
        if (resp.success && resp.data) {
          setSlugCheck({
            status: resp.data.available ? 'available' : 'taken',
            suggestion: resp.data.suggestion ?? null,
          });
        } else {
          setSlugCheck({ status: 'idle', suggestion: null });
        }
      } catch {
        setSlugCheck({ status: 'idle', suggestion: null });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [slug]);

  const handleNext = async () => {
    setError(null);

    const trimmedSlug = slug.trim().toLowerCase();
    // Slug is the entry point to the booking page, so it's the one field
    // we don't let the user wave through with an obviously invalid value.
    if (trimmedSlug && (trimmedSlug.length < 3 || slugCheck.status === 'taken')) {
      setError('Pick an available booking slug, or skip this step.');
      return;
    }

    setSaving(true);
    try {
      const profileResp = await usersApi.updateProfile({
        slug: trimmedSlug || null,
        timezone,
      });
      if (!profileResp.success || !profileResp.data) {
        throw new Error(profileResp.error || 'Failed to save profile.');
      }

      const availabilityResp = await availabilityApi.put({
        // Mirror Settings: only persist active days, but keep inactive ones
        // in the list with their hours so the UI can re-check them later.
        days,
        blockedDates: availability.blockedDates,
      });
      if (!availabilityResp.success || !availabilityResp.data) {
        throw new Error(availabilityResp.error || 'Failed to save availability.');
      }

      onSaved(profileResp.data, availabilityResp.data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepLayout
      title="Set up your booking page"
      subtitle="Clients self-schedule on a public page at /book/your-slug. Choose a slug and the hours you accept bookings."
      onPrimary={handleNext}
      primaryLabel="Save & continue"
      primaryLoading={saving}
      onSecondary={onSkip}
      secondaryLabel="Skip for now"
      error={error}
    >
      <div className="flex flex-col gap-5">
        <Field
          label="Booking slug"
          // Field renders `error` in red and replaces hint when present, so
          // route the "taken" message through error and let hint cover the
          // valid/idle states. The clickable suggestion below isn't part of
          // the error string — it's a separate button so the user can apply
          // the suggestion in one click.
          error={slugCheck.status === 'taken' ? 'That slug is already taken.' : undefined}
          hint={
            slugCheck.status === 'available'
              ? '✓ Available'
              : slugCheck.status === 'checking'
                ? 'Checking availability…'
                : 'Lowercase letters, numbers, and single hyphens. 3–100 characters.'
          }
        >
          {/* Stack on mobile: the origin prefix is ~30+ chars and was leaving
              the slug input squeezed into ~50px on iPhone portrait. Side-by-
              side returns at sm+. Origin pulled from getPublicOrigin so the
              preview reads `https://flowspect.app/book/…` even when the
              WebView's actual origin is `capacitor://…`. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <span className="text-body-sm text-ink-subtle font-mono whitespace-nowrap break-all sm:break-normal">
              {getPublicOrigin()}/book/
            </span>
            <Input
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              onBlur={() => {
                if (!slugTouched && profile.companyName) setSlug(slugify(profile.companyName));
              }}
              placeholder="your-name"
              invalid={slugCheck.status === 'taken'}
              className="font-mono min-w-0 flex-1"
            />
          </div>
          {slugCheck.status === 'taken' && slugCheck.suggestion && (
            <button
              type="button"
              onClick={() => {
                setSlug(slugCheck.suggestion!);
                setSlugTouched(true);
              }}
              className="self-start text-caption text-brand underline mt-1 font-mono"
            >
              Use {slugCheck.suggestion}
            </button>
          )}
        </Field>

        <Field label="Timezone" hint="Your weekly hours are interpreted in this zone.">
          <Input
            list="onboarding-tz-options"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Los_Angeles"
            className="font-mono"
          />
          <datalist id="onboarding-tz-options">
            {TIMEZONE_OPTIONS.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
        </Field>

        <div>
          <p className="text-body-sm font-medium text-ink mb-2">Weekly schedule</p>
          <div className="space-y-2">
            {DAYS_OF_WEEK.map((dow) => {
              const day = days.find((d) => d.dayOfWeek === dow.value)!;
              const timeInputCls =
                'h-9 px-2 text-body-sm bg-surface-1 text-ink border border-border rounded-md ' +
                'focus:border-brand focus:shadow-ring-brand focus:outline-none ' +
                'disabled:bg-surface-2 disabled:text-ink-faint';
              return (
                // flex-wrap so the row degrades cleanly on iPhone widths
                // (checkbox + label on the first row, time inputs wrapping
                // to the next). On sm+ everything stays on a single row.
                <div key={dow.value} className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-2 shrink-0 w-24 sm:w-32">
                    <input
                      type="checkbox"
                      checked={day.isActive}
                      onChange={(e) =>
                        setDays((prev) =>
                          prev.map((d) =>
                            d.dayOfWeek === dow.value
                              ? { ...d, isActive: e.target.checked }
                              : d
                          )
                        )
                      }
                    />
                    <span className="text-body-sm text-ink">{dow.label}</span>
                  </label>
                  <input
                    type="time"
                    value={day.startTime}
                    disabled={!day.isActive}
                    onChange={(e) =>
                      setDays((prev) =>
                        prev.map((d) =>
                          d.dayOfWeek === dow.value
                            ? { ...d, startTime: e.target.value }
                            : d
                        )
                      )
                    }
                    className={timeInputCls + ' flex-1 min-w-[88px]'}
                  />
                  <span className="text-body-sm text-ink-subtle">to</span>
                  <input
                    type="time"
                    value={day.endTime}
                    disabled={!day.isActive}
                    onChange={(e) =>
                      setDays((prev) =>
                        prev.map((d) =>
                          d.dayOfWeek === dow.value ? { ...d, endTime: e.target.value } : d
                        )
                      )
                    }
                    className={timeInputCls + ' flex-1 min-w-[88px]'}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </StepLayout>
  );
}
